import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, ToggleButtonGroup, ToggleButton, CircularProgress, Alert, useTheme } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, AreaChart, Area } from 'recharts';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import { selectLabels } from 'state/dictionary/dictionarySlice';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import {
  getIuChartAccentSeriesColor,
  getIuChartAccentSeriesFill,
  getIuChartTooltipContentStyle,
} from './instantUpdateChartTheme';

type TimeRange = '7d' | '30d' | '90d';

interface Row {
  date: string;
  activatedCount: number;
  failedCount: number;
  completedCount: number;
  quotaExceededCount: number;
  blockedCount: number;
}
interface Resp { timeRange: TimeRange; timeSeriesData: Row[] }

export const SchedulerStatsGraph: React.FC = () => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const [chartType, setChartType] = useState<'line' | 'bar' | 'area'>('area');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const labels = useSelector((state: RootState) => selectLabels(state));
  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const resp = await apiClient.get(`/iu/analytics/scheduler-stats?timeRange=${timeRange}`);
      const data = resp.data as Resp;
      setRows(data.timeSeriesData || []);
    } catch (e: any) {
      const msg = e?.message || 'Failed to load scheduler stats';
      setError(msg); Logger.error('SchedulerStatsGraph', msg);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [timeRange]);

  const renderChart = () => {
    const tooltipStyle = getIuChartTooltipContentStyle(theme);
    const blockedColor = getIuChartAccentSeriesColor(theme, theme.palette.secondary.main);
    const blockedFill = getIuChartAccentSeriesFill(theme, theme.palette.secondary.light);
    const commonProps = { data: rows, margin: { top: 10, right: 20, left: 0, bottom: 0 } } as const;
    const lines = (
      <>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" />
        <YAxis allowDecimals={false} />
        <Tooltip contentStyle={tooltipStyle} />
        <Legend />
        <Line type="monotone" dataKey="activatedCount" stroke={theme.palette.success.main} dot={false} name="Activated" />
        <Line type="monotone" dataKey="failedCount" stroke={theme.palette.error.main} dot={false} name="Failed" />
        <Line type="monotone" dataKey="quotaExceededCount" stroke={theme.palette.warning.main} dot={false} name="Quota Exceeded" />
        <Line type="monotone" dataKey="blockedCount" stroke={blockedColor} dot={false} name="Blocked" />
      </>
    );
    const bars = (
      <>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" />
        <YAxis allowDecimals={false} />
        <Tooltip contentStyle={tooltipStyle} />
        <Legend />
        <Bar dataKey="activatedCount" fill={theme.palette.success.main} name="Activated" />
        <Bar dataKey="failedCount" fill={theme.palette.error.main} name="Failed" />
        <Bar dataKey="quotaExceededCount" fill={theme.palette.warning.main} name="Quota Exceeded" />
        <Bar dataKey="blockedCount" fill={blockedColor} name="Blocked" />
      </>
    );
    const areas = (
      <>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" />
        <YAxis allowDecimals={false} />
        <Tooltip contentStyle={tooltipStyle} />
        <Legend />
        <Area type="monotone" dataKey="activatedCount" stroke={theme.palette.success.main} fill={theme.palette.success.light} name="Activated" />
        <Area type="monotone" dataKey="failedCount" stroke={theme.palette.error.main} fill={theme.palette.error.light} name="Failed" />
        <Area type="monotone" dataKey="quotaExceededCount" stroke={theme.palette.warning.main} fill={theme.palette.warning.light} name="Quota Exceeded" />
        <Area type="monotone" dataKey="blockedCount" stroke={blockedColor} fill={blockedFill} name="Blocked" />
      </>
    );
    if (chartType === 'bar') return (<BarChart {...commonProps}>{bars}</BarChart>);
    if (chartType === 'area') return (<AreaChart {...commonProps}>{areas}</AreaChart>);
    return (<LineChart {...commonProps}>{lines}</LineChart>);
  };

  return (
    <Card
      elevation={theme.palette.dashboard?.gaugePanelBackgroundColor ? 0 : 1}
      sx={{
        mb: 4,
        borderRadius: 3,
        ...(theme.palette.dashboard?.gaugePanelBackgroundColor
          ? {
              bgcolor: theme.palette.dashboard.gaugePanelBackgroundColor,
              boxShadow: theme.palette.dashboard.gaugePanelBoxShadow ?? 3,
            }
          : {
              bgcolor: (t: any) =>
                t.palette.mode === 'dark' ? 'rgb(20, 25, 55)' : t.palette.grey[100],
            }),
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{labels?.['modules-instantupdate-dashboard-scheduler-stats-graph-title'] ?? 'Campaign Scheduler Stats'}</Typography>
          <Box display="flex" gap={1}>
            <ToggleButtonGroup size="small" exclusive value={chartType} onChange={(_e, v) => v && setChartType(v)}>
              <ToggleButton value="line">Line</ToggleButton>
              <ToggleButton value="bar">Bar</ToggleButton>
              <ToggleButton value="area">Area</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={timeRange} onChange={(_e, v) => v && setTimeRange(v)}>
              <ToggleButton value="7d">7d</ToggleButton>
              <ToggleButton value="30d">30d</ToggleButton>
              <ToggleButton value="90d">90d</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={160}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="textSecondary">No data in selected range.</Typography>
        ) : (
          <Box height={320}>
            <ResponsiveContainer>
              {renderChart()}
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
